import { cx } from '@emotion/css';
import { RateReviewTwoTone } from '@mui/icons-material';
import { Button } from '@sortlist-frontend/design-system';
import { Divider, Rating } from '@sortlist-frontend/design-system/legacy';
import { AiReviewSummary, getFormattedRating, ReviewCard } from '@sortlist-frontend/shared-components';
import { Trans, useTranslation } from '@sortlist-frontend/translation/ssr';
import { Fragment, useState } from 'react';

import { Obfuscate } from '_components/common/Obfuscate/Obfuscate';
import { ToggleButton } from '_components/common/ToggleButton';
import { usePublicAppContext } from '_core/context/public-app-context';
import * as S from '_features/AgencyProfile/components/ReviewsPanel/styles';
import { SectionBox } from '_features/AgencyProfile/components/SectionBox';
import { agencyProfileConfig } from '_features/AgencyProfile/config';
import { useGetAgencyProfile } from '_features/AgencyProfile/hooks/useGetAgencyProfile';
import { useGetAgencyReviews } from '_features/AgencyProfile/hooks/useGetAgencyReviews';

import type { ReviewNode } from './types';
import getReportReviewUrl from './utils';

const INITIAL_DISPLAYED_REVIEWS = 3;

const mapReviewNodeToReviewCard = (node: ReviewNode, agencySlug: string) => {
  const companySize = node.companySize as number[] | null;
  return {
    agencySlug,
    slug: node.slug,
    rating: node.rating ?? 0,
    userFullName: node.userFullName ?? null,
    userCompanyName: node.userCompanyName ?? null,
    userPictureUrl: node.userPictureUrl ?? null,
    userJobTitle: node.userJobTitle ?? null,
    ratingBudget: node.ratingBudget ?? null,
    ratingCollaboration: node.ratingCollaboration ?? null,
    ratingQuality: node.ratingQuality ?? null,
    ratingSchedule: node.ratingSchedule ?? null,
    descriptionSummary: node.descriptionSummary ?? null,
    descriptionCons: node.descriptionCons ?? null,
    descriptionPros: node.descriptionPros ?? null,
    recommended: node.recommended ?? false,
    reply: node.reply ?? null,
    companySizeMin: companySize?.[0] ?? null,
    companySizeMax: companySize?.[1] ?? null,
    expertiseId: null,
    expertiseName: node.service?.expertise?.name ?? null,
    sectorName: node.sector?.name ?? null,
    serviceId: null,
    verifiedAt: node.verifiedAt ?? null,
    updatedAt: node.updatedAt,
    createdAt: node.createdAt,
  };
};

export const ReviewsPanel = () => {
  const { t } = useTranslation(agencyProfileConfig.i18nNamespaces);
  const { locale, pageData, getUrl } = usePublicAppContext();
  const { agencySlug } = pageData as { agencySlug: string };
  const { data: { agency } = {} } = useGetAgencyProfile({ agencySlug, locale: locale as string });
  const {
    reviews: reviewsData,
    hasNextPage,
    isFetchingMore,
    fetchMoreReviews,
  } = useGetAgencyReviews({ agencySlug, locale: locale as string });
  const { name, paidMember } = { ...agency };

  const reviewNodes = reviewsData?.nodes?.filter((node) => node != null) ?? [];
  const totalCount = reviewsData?.totalCount ?? 0;
  const averageRating = reviewsData?.averageRating ?? null;

  const [toggledAllReviews, setToggledAllReviews] = useState(false);

  return (
    <SectionBox
      id="reviews"
      title={t('agency:profile.reviews.title')}
      className={'sm:py-24 max-sm:py-16'}
      subtitle={
        totalCount === 0 ? (
          <Trans i18nKey={'agency:profile.reviews.subtitleNoReview'} values={{ name }}>
            No review for {'{{name}}'} yet
          </Trans>
        ) : (
          <Trans i18nKey={'agency:profile.reviews.subtitle'} count={totalCount} values={{ count: totalCount, name }}>
            <b>{'{{count}}'} reviews</b>
            for {'{{name}}'}
          </Trans>
        )
      }
      childrenHeader={
        averageRating != null && totalCount > 0 ? (
          <div className="layout-column layout-align-xs-start-start layout-align-start-end max-sm:mt-8">
            <div>
              <span className="bold h2">{getFormattedRating(averageRating)}</span>
              <span>{'/5'}</span>
            </div>
            <div>
              <Rating rate={getFormattedRating(averageRating)} size={20}>
                <span className="ml-1 lh-2">{`(${t('agency:profile.reviews.count', {
                  count: totalCount,
                })})`}</span>
              </Rating>
            </div>
          </div>
        ) : null
      }>
      <Fragment>
        {agency && reviewNodes.length > 0 ? (
          <div className="lg:px-16 px-8">
            {paidMember === true && agency?.aiReviewSummary != null && (
              <AiReviewSummary
                className="my-4"
                aiSummary={agency.aiReviewSummary}
                hideContent={false}
                reviewsCount={totalCount}
              />
            )}
            <S.Shape className="border border-secondary-900 sm:mb-8 rounded-lg shadow-2 overflow-hidden">
              {reviewNodes
                .slice(0, toggledAllReviews ? reviewNodes.length : INITIAL_DISPLAYED_REVIEWS)
                .map((node, index: number) => {
                  const review = mapReviewNodeToReviewCard(node, agencySlug);
                  const { userFullName, slug } = review;

                  return (
                    <S.ReviewContainer
                      key={userFullName}
                      className={cx({ 'max-sm:mb-4': index + 1 != reviewNodes.length })}>
                      <ReviewCard
                        reportUrl={getReportReviewUrl(userFullName, agency.slug ?? '', slug)}
                        review={review}
                        agencyName={agency.name ?? ''}
                      />
                      {reviewNodes.length !== index + 1 && <Divider className="hide-xs" />}
                    </S.ReviewContainer>
                  );
                })}
              {hasNextPage === true && toggledAllReviews ? (
                <Fragment>
                  <Divider />
                  <div className="layout-row layout-align-center-center py-4">
                    <Button
                      variant="light"
                      className="underline"
                      color="secondary"
                      size="sm"
                      isLoading={isFetchingMore}
                      onPress={fetchMoreReviews}>
                      {t('common:clampLines.seeMore')}
                    </Button>
                  </div>
                </Fragment>
              ) : null}
              {totalCount > INITIAL_DISPLAYED_REVIEWS && (!toggledAllReviews || hasNextPage === false) ? (
                <Fragment>
                  {toggledAllReviews && <Divider />}
                  <div className="layout-column layout-align-start-stretch">
                    <ToggleButton
                      handleToggled={() => setToggledAllReviews(!toggledAllReviews)}
                      seeMoreBtnLabel={
                        <Trans i18nKey={'agency:profile.reviews.seeOtherReviews'} count={totalCount}>
                          See all other reviews ({{ count: totalCount - INITIAL_DISPLAYED_REVIEWS }})
                        </Trans>
                      }
                      seeLessBtnLabel={t('agency:profile.reviews.seeLessReviews')}
                      toggled={toggledAllReviews}
                    />
                  </div>
                </Fragment>
              ) : null}
            </S.Shape>
          </div>
        ) : null}
        <div className="lg:px-16 px-8 pt-8">
          <S.ReviewCTA className="layout-column layout-gt-xs-row layout-align-center-center rounded-lg bg-secondary-50 flex-1">
            <div className="p-4 layout-column layout-align-center-center">
              <RateReviewTwoTone style={{ fontSize: 24 }} className="text-secondary-700" />
            </div>
            <div className="flex-1 layout-colum py-4 px-0">
              <span className="bold mb-1">
                <Trans i18nKey={'agency:profile.reviews.postReview.title'}>Worked with {{ agencyName: name }}?</Trans>
              </span>
              <p className="small text-secondary-500">{t('agency:profile.reviews.postReview.subtitle')}</p>
            </div>
            <div className="p-4">
              <Obfuscate
                className="display-block btn btn-md btn-light btn-secondary"
                href={getUrl('agency.profile.review', { slug: agencySlug })}
                obfuscate={true}>
                {t('agency:profile.reviews.postReview.ctaLabel')}
              </Obfuscate>
            </div>
          </S.ReviewCTA>
        </div>
      </Fragment>
    </SectionBox>
  );
};
